import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Toolbar,
  Typography,
  Button,
  Stack,
  TextField,
  Autocomplete,
  Chip,
  InputAdornment,
  Collapse,
  Grid
} from '@mui/material';
import toast from 'react-hot-toast';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import IconButton from '@mui/material/IconButton';

import {
  retrieveAllBlendsApi,
  retrieveBlendByIdApi,
  retrieveBlendVersionsApi,
  retrieveVersionItemsApi,
  createBlendApi,
  createBlendVersionApi,
  createVersionItemApi,
  updateBlendVersionApi,
  updateBlendApi,
  updateVersionItemApi,
  deleteBlendApi,
  deleteBlendVersionApi,
  deleteVersionItemApi
} from "../../../components/api/BlendApiService";
import { retrieveAllItemsApi } from '../../../components/api/ItemApiService';
import { retrieveBatchesByBlendVersionApi } from '../../../components/api/BatchApiService';
import {
  retrieveFastFilteredShippingInstructionsApi,
  retrieveFastShippingInstructionsApi,
  retrieveFastShippingInstructionCostingApi,
  retrieveShippingInstructionByIdApi
} from '../../../components/api/ShippingInstructionApiService';

import VersionAccordion from './VersionAccordion';
import AddItemDialog from './AddItemDialog';
import {
  CreateBlendDialog,
  EditBlendDialog,
  CreateVersionDialog,
  EditVersionDialog,
  DuplicateVersionDialog,
  EditItemDialog
} from './BlendDialogs';

const mapVersionItem = (item) => ({
  id: item.id,
  itemId: item.item?.id || 'N/A',
  buyingGrade: item.item?.buyingGrade || '',
  origin: item.item?.origin || '',
  crop: item.item?.crop || '',
  nicotine: item.item?.nicotine || "N/A",
  sugar: item.item?.sugar || "N/A",
  partNumber: item.partNumber,
  customerGradeMark: item?.shippingInstruction?.shipmentRequest?.customerGradeMark,
  itemName: (item.item?.origin ? (item.item.origin+" ") : "") + (item.item?.type ? (item.item.type+" ") : "")+ (item.item?.buyingGrade ? (item.item.buyingGrade+" ") : "")+ (item.item?.crop ? (item.item.crop+" ") : ""),
  quantity: Number(item.quantity) || 0,
  percentage: 0,
  unit: item.unit,
  cost: item.cost,
  supplier: item.supplier,
  shippingInstructionId: item.shippingInstruction?.id || null,
  shippingInstructionNumber: item.shippingInstruction?.finalShippingInstructionNumber || '',
  netWeightPerCase: item.shippingInstruction?.shipmentRequest?.netWeightPerCase || null
});

export default function BlendView() {
  const [loading, setLoading] = useState(false);
  const [blends, setBlends] = useState([]);
  const [selectedBlend, setSelectedBlend] = useState('');
  const [selectedBlendDetails, setSelectedBlendDetails] = useState(null);
  const [versions, setVersions] = useState([]);
  const [itemsByVersion, setItemsByVersion] = useState({});
  const [expandedVersionId, setExpandedVersionId] = useState(false);
  const [openBlendDialog, setOpenBlendDialog] = useState(false);
  const [openVersionDialog, setOpenVersionDialog] = useState(false);
  const [openItemDialog, setOpenItemDialog] = useState(false);
  const [newBlend, setNewBlend] = useState({ code: '', description: '' });
  const [newVersion, setNewVersion] = useState({
    versionName: '',
    description: '',
    status: 'Actual',
    cutWidth: '',
    moisture: '',
    temperature: '',
    nicotineTolerancePct: '',
    sugarTolerancePct: ''
  });
  const [newItem, setNewItem] = useState({
    description: '',
    quantity: '',
    unit: 'KG',
    item: null,
    itemId: null,
    shippingInstruction: null,
    shippingInstructionId: null
  });
  const [editBlendDialog, setEditBlendDialog] = useState(false);
  const [editVersionDialog, setEditVersionDialog] = useState(false);
  const [editItemDialog, setEditItemDialog] = useState(false);
  const [selectedItemForEdit, setSelectedItemForEdit] = useState(null);
  const [selectedVersionForEdit, setSelectedVersionForEdit] = useState(null);
  const [availableItems, setAvailableItems] = useState([]);
  const [availableShippingInstructions, setAvailableShippingInstructions] = useState([]);
  const [playgroundAvailableItems, setPlaygroundAvailableItems] = useState([]);
  const [addItemTab, setAddItemTab] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [excelImportRows, setExcelImportRows] = useState([]);
  const [validatingExcel, setValidatingExcel] = useState(false);

  // Filter states
  const [versionFilter, setVersionFilter] = useState({
    name: '',
    status: '',
    dateFrom: '',
    dateTo: '',
    selectedItems: [],
    selectedShippingInstructions: []
  });
  const [blendFilter, setBlendFilter] = useState({
    selectedItems: [],
    selectedShippingInstructions: []
  });
  const [itemFilter, setItemFilter] = useState({
    itemId: '',
    customerGradeMark: '',
    minQuantity: '',
    maxQuantity: ''
  });
  const [blendFilterIndex, setBlendFilterIndex] = useState({});
  const [showBlendFilters, setShowBlendFilters] = useState(false);
  const [loadingBlendFilterData, setLoadingBlendFilterData] = useState(false);
  const [loadingVersionFilterData, setLoadingVersionFilterData] = useState(false);
  const [blendFilterOptions, setBlendFilterOptions] = useState({ items: [], shippingInstructions: [] });
  const [versionFilterOptions, setVersionFilterOptions] = useState({ items: [], shippingInstructions: [] });
  const [showVersionFilters, setShowVersionFilters] = useState(false);
  const [showItemFilters, setShowItemFilters] = useState(false);
  const [duplicateVersionDialog, setDuplicateVersionDialog] = useState(false);
  const [versionToDuplicate, setVersionToDuplicate] = useState(null);
  const [activeBatchVersionIds, setActiveBatchVersionIds] = useState(new Set());
  const [duplicateVersionName, setDuplicateVersionName] = useState('');
  const [createFromVersion, setCreateFromVersion] = useState(false);
  const [selectedBaseVersion, setSelectedBaseVersion] = useState(null);
  const lastAutoExpandTriggerRef = useRef('');

  // ─── Helpers ─────────────────────────────────────────────────────────
  const resetNewItem = () => {
    setNewItem({
      description: '',
      quantity: '',
      unit: 'KG',
      item: null,
      itemId: null,
      shippingInstruction: null,
      shippingInstructionId: null
    });
  };

  const resetNewVersion = () => {
    setNewVersion({
      versionName: '',
      description: '',
      status: 'Actual',
      cutWidth: '',
      moisture: '',
      temperature: '',
      nicotineTolerancePct: '',
      sugarTolerancePct: ''
    });
    setCreateFromVersion(false);
    setSelectedBaseVersion(null);
  };

  const makeItemKey = (item) => {
    if (!item || item.itemId == null) return '';
    return `${item.itemId}|${item.buyingGrade || ''}|${item.origin || ''}|${item.crop || ''}`;
  };

  const makeShippingInstructionKey = (item) => {
    if (!item) return '';
    const idPart = item.shippingInstructionId != null ? String(item.shippingInstructionId) : '';
    const numberPart = item.shippingInstructionNumber || '';
    if (!idPart && !numberPart) return '';
    return `${idPart}|${numberPart}`;
  };

  const itemOptionFromRow = (row) => {
    const key = makeItemKey(row);
    if (!key) return null;
    return {
      key,
      itemId: row.itemId,
      buyingGrade: row.buyingGrade || '',
      origin: row.origin || '',
      crop: row.crop || '',
      label: `${row.itemId} | ${row.buyingGrade || '-'} | ${row.origin || '-'} | ${row.crop || '-'}`
    };
  };

  const siOptionFromRow = (row) => {
    const key = makeShippingInstructionKey(row);
    if (!key) return null;
    const idPart = row.shippingInstructionId != null ? String(row.shippingInstructionId) : '-';
    const numberPart = row.shippingInstructionNumber || '-';
    return {
      key,
      id: row.shippingInstructionId,
      number: row.shippingInstructionNumber || '',
      label: `${idPart} | ${numberPart}`
    };
  };

  const matchesItemAndSiFilters = (items, selectedItems, selectedSis) => {
    const selectedItemKeySet = new Set((selectedItems || []).map((option) => option.key));
    const selectedSiKeySet = new Set((selectedSis || []).map((option) => option.key));

    return (items || []).some((item) => {
      const itemMatch = selectedItemKeySet.size === 0 || selectedItemKeySet.has(makeItemKey(item));
      const siMatch = selectedSiKeySet.size === 0 || selectedSiKeySet.has(makeShippingInstructionKey(item));

      return itemMatch && siMatch;
    });
  };

  // ─── Filter logic ────────────────────────────────────────────────────
  const filteredVersions = versions.filter(version => {
    if (versionFilter.name && !version.versionName.toLowerCase().includes(versionFilter.name.toLowerCase())) return false;
    if (versionFilter.status && (version.status || 'Actual') !== versionFilter.status) return false;
    if (versionFilter.dateFrom || versionFilter.dateTo) {
      const versionDate = new Date(version.timestamp);
      if (versionFilter.dateFrom && versionDate < new Date(versionFilter.dateFrom)) return false;
      if (versionFilter.dateTo && versionDate > new Date(versionFilter.dateTo)) return false;
    }
    if (versionFilter.selectedItems.length || versionFilter.selectedShippingInstructions.length) {
      const hasLoadedItems = Object.prototype.hasOwnProperty.call(itemsByVersion, version.id);
      if (!hasLoadedItems) return true;
      const versionItems = itemsByVersion[version.id] || [];
      if (!matchesItemAndSiFilters(versionItems, versionFilter.selectedItems, versionFilter.selectedShippingInstructions)) return false;
    }
    return true;
  });

  const filteredBlends = blends.filter((blend) => {
    if (!blendFilter.selectedItems.length && !blendFilter.selectedShippingInstructions.length) return true;

    const index = blendFilterIndex[blend.id];
    if (!index) return true;

    const selectedItemKeySet = new Set(blendFilter.selectedItems.map((option) => option.key));
    const selectedSiKeySet = new Set(blendFilter.selectedShippingInstructions.map((option) => option.key));

    const itemMatch =
      selectedItemKeySet.size === 0 ||
      (index.itemKeys || []).some((itemKey) => selectedItemKeySet.has(itemKey));
    const siMatch =
      selectedSiKeySet.size === 0 ||
      (index.siKeys || []).some((siKey) => selectedSiKeySet.has(siKey));

    return itemMatch && siMatch;
  });

  const getFilteredItems = (versionId) => {
    const items = itemsByVersion[versionId] || [];
    return items.filter(item => {
      if (itemFilter.itemId && !item.itemId.toString().toLowerCase().includes(itemFilter.itemId.toLowerCase())) return false;
      if (itemFilter.customerGradeMark &&
          (!item.customerGradeMark || !item.customerGradeMark.toLowerCase().includes(itemFilter.customerGradeMark.toLowerCase()))) return false;
      const quantity = Math.abs(Number(item.quantity) || 0);
      if (itemFilter.minQuantity && quantity < Number(itemFilter.minQuantity)) return false;
      if (itemFilter.maxQuantity && quantity > Number(itemFilter.maxQuantity)) return false;
      return true;
    });
  };

  const clearBlendFilters = () => setBlendFilter({ selectedItems: [], selectedShippingInstructions: [] });
  const clearVersionFilters = () => setVersionFilter({ name: '', status: '', dateFrom: '', dateTo: '', selectedItems: [], selectedShippingInstructions: [] });
  const clearItemFilters = () => setItemFilter({ itemId: '', customerGradeMark: '', minQuantity: '', maxQuantity: '' });
  const clearAllFilters = () => { clearBlendFilters(); clearVersionFilters(); clearItemFilters(); setShowBlendFilters(false); setShowVersionFilters(false); setShowItemFilters(false); };
  const hasActiveBlendFilters = () => !!(blendFilter.selectedItems.length || blendFilter.selectedShippingInstructions.length);
  const hasActiveVersionFilters = () => !!(versionFilter.name || versionFilter.status || versionFilter.dateFrom || versionFilter.dateTo || versionFilter.selectedItems.length || versionFilter.selectedShippingInstructions.length);
  const hasActiveItemFilters = () => !!(itemFilter.itemId || itemFilter.customerGradeMark || itemFilter.minQuantity || itemFilter.maxQuantity);

  const versionFilterTrigger = useMemo(() => JSON.stringify({
    blendId: selectedBlend || '',
    name: versionFilter.name || '',
    status: versionFilter.status || '',
    dateFrom: versionFilter.dateFrom || '',
    dateTo: versionFilter.dateTo || '',
    itemKeys: (versionFilter.selectedItems || []).map((opt) => opt.key).sort(),
    siKeys: (versionFilter.selectedShippingInstructions || []).map((opt) => opt.key).sort()
  }), [
    selectedBlend,
    versionFilter.name,
    versionFilter.status,
    versionFilter.dateFrom,
    versionFilter.dateTo,
    versionFilter.selectedItems,
    versionFilter.selectedShippingInstructions
  ]);

  // Escape to clear all filters
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') { clearBlendFilters(); clearVersionFilters(); clearItemFilters(); setShowBlendFilters(false); setShowVersionFilters(false); setShowItemFilters(false); }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    const loadVersionFilterData = async () => {
      if (!selectedBlend || versions.length === 0) return;
      if (!versionFilter.selectedItems.length && !versionFilter.selectedShippingInstructions.length && !showVersionFilters) return;

      const missingVersions = versions.filter((version) => !itemsByVersion[version.id]);
      if (missingVersions.length === 0) return;

      setLoadingVersionFilterData(true);
      try {
        const fetched = await Promise.all(
          missingVersions.map(async (version) => {
            const response = await retrieveVersionItemsApi(selectedBlend, Number(version.id));
            return { versionId: version.id, rows: (response.data || []).map(mapVersionItem) };
          })
        );

        setItemsByVersion((prev) => {
          const next = { ...prev };
          fetched.forEach(({ versionId, rows }) => {
            next[versionId] = rows;
          });
          return next;
        });
      } catch (error) {
        console.error('Error loading version items for filters:', error);
        toast.error('Failed to load version item filters');
      } finally {
        setLoadingVersionFilterData(false);
      }
    };

    loadVersionFilterData();
  }, [selectedBlend, versions, versionFilter.selectedItems, versionFilter.selectedShippingInstructions, showVersionFilters]);

  useEffect(() => {
    const optionItems = [];
    const optionSis = [];
    versions.forEach((version) => {
      const rows = itemsByVersion[version.id] || [];
      rows.forEach((row) => {
        const itemOption = itemOptionFromRow(row);
        const siOption = siOptionFromRow(row);
        if (itemOption) optionItems.push(itemOption);
        if (siOption) optionSis.push(siOption);
      });
    });

    const uniqueItems = Array.from(new Map(optionItems.map((opt) => [opt.key, opt])).values());
    const uniqueSis = Array.from(new Map(optionSis.map((opt) => [opt.key, opt])).values());
    setVersionFilterOptions({ items: uniqueItems, shippingInstructions: uniqueSis });
  }, [versions, itemsByVersion]);

  useEffect(() => {
    const loadBlendFilterData = async () => {
      if (!blendFilter.selectedItems.length && !blendFilter.selectedShippingInstructions.length && !showBlendFilters) return;
      if (blends.length === 0) return;

      const missingBlends = blends.filter((blend) => !blendFilterIndex[blend.id]);
      if (missingBlends.length === 0) return;

      setLoadingBlendFilterData(true);
      try {
        const nextIndex = {};
        for (const blend of missingBlends) {
          const versionResponse = await retrieveBlendVersionsApi(blend.id);
          const blendVersions = versionResponse.data || [];

          const itemKeys = new Set();
          const siKeys = new Set();
          const itemOptions = new Map();
          const siOptions = new Map();

          for (const version of blendVersions) {
            const itemResponse = await retrieveVersionItemsApi(blend.id, version.id);
            const mappedItems = (itemResponse.data || []).map(mapVersionItem);

            mappedItems.forEach((row) => {
              const itemOption = itemOptionFromRow(row);
              const siOption = siOptionFromRow(row);
              if (itemOption) {
                itemKeys.add(itemOption.key);
                itemOptions.set(itemOption.key, itemOption);
              }
              if (siOption) {
                siKeys.add(siOption.key);
                siOptions.set(siOption.key, siOption);
              }
            });
          }

          nextIndex[blend.id] = {
            itemKeys: Array.from(itemKeys),
            siKeys: Array.from(siKeys),
            itemOptions: Array.from(itemOptions.values()),
            siOptions: Array.from(siOptions.values())
          };
        }

        const mergedIndex = { ...blendFilterIndex, ...nextIndex };
        setBlendFilterIndex(mergedIndex);

        const allItemOptions = [];
        const allSiOptions = [];
        Object.values(mergedIndex).forEach((entry) => {
          allItemOptions.push(...(entry.itemOptions || []));
          allSiOptions.push(...(entry.siOptions || []));
        });

        const uniqueItems = Array.from(new Map(allItemOptions.map((opt) => [opt.key, opt])).values());
        const uniqueSis = Array.from(new Map(allSiOptions.map((opt) => [opt.key, opt])).values());
        setBlendFilterOptions({ items: uniqueItems, shippingInstructions: uniqueSis });
      } catch (error) {
        console.error('Error loading blend filter data:', error);
        toast.error('Failed to load blend filter data');
      } finally {
        setLoadingBlendFilterData(false);
      }
    };

    loadBlendFilterData();
  }, [blendFilter.selectedItems, blendFilter.selectedShippingInstructions, blends, showBlendFilters]);

  useEffect(() => {
    if (!selectedBlend || !hasActiveVersionFilters()) {
      lastAutoExpandTriggerRef.current = '';
      return;
    }
    if (filteredVersions.length === 0) return;
    if (lastAutoExpandTriggerRef.current === versionFilterTrigger) return;

    const firstMatching = filteredVersions[0];
    if (!firstMatching) return;
    setExpandedVersionId(firstMatching.id);
    handleAccordionExpand(firstMatching.id);
    lastAutoExpandTriggerRef.current = versionFilterTrigger;

    const versionEl = document.getElementById(`blend-version-${firstMatching.id}`);
    if (versionEl) versionEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [selectedBlend, filteredVersions, versionFilterTrigger]);

  // ─── Data utilities ──────────────────────────────────────────────────
  const hasActiveRecordStatus = (si) => String(si?.recordStatus ?? 'ACTIVE').toUpperCase() !== 'PASSIVE';

  const hasPositiveStorageQuantity = (si) => {
    if (!hasActiveRecordStatus(si)) return false;
    const raw = si?.shipmentRequestStorageQuantity;
    if (raw == null) return false;
    const value = Number(raw);
    return !Number.isNaN(value) && value > 0;
  };

  const sortShippingInstructionsById = (items = []) =>
    [...items].sort((a, b) => {
      const aId = Number(a?.id ?? Number.MAX_SAFE_INTEGER);
      const bId = Number(b?.id ?? Number.MAX_SAFE_INTEGER);
      if (Number.isNaN(aId) && Number.isNaN(bId)) return String(a?.id ?? '').localeCompare(String(b?.id ?? ''));
      if (Number.isNaN(aId)) return 1;
      if (Number.isNaN(bId)) return -1;
      return aId - bId;
    });

  const dedupeById = (list) => {
    const map = new Map();
    (list || []).forEach((item) => { if (item?.id == null) return; map.set(item.id, item); });
    return Array.from(map.values());
  };

  // ─── Data fetching ───────────────────────────────────────────────────
  const retrieveItems = async () => {
    try {
      const response = await retrieveAllItemsApi();
      setAvailableItems(response.data);
    } catch (error) {
      console.error('Error loading items:', error);
      toast.error('Failed to load items');
    }
  };

  const retrieveShippingInstructions = async (selectedItemId = null) => {
    try {
      const allResponse = await retrieveFastShippingInstructionsApi();
      let all = allResponse?.data || [];

      if (selectedItemId) {
        try {
          const filterDto = { itemIdList: [selectedItemId] };
          const filteredResponse = await retrieveFastFilteredShippingInstructionsApi(filterDto);
          const filtered = filteredResponse?.data || [];
          all = dedupeById([...filtered, ...all]);
        } catch (innerError) {
          console.warn('Failed to filter shipping instructions by item, falling back to all:', innerError);
        }
      }

      const filteredByStorage = sortShippingInstructionsById(all.filter(hasPositiveStorageQuantity));
      setAvailableShippingInstructions(filteredByStorage);
      return filteredByStorage;
    } catch (error) {
      console.error('Error loading shipping instructions:', error);
      toast.error('Failed to load shipping instructions');
      return [];
    }
  };

  const retrievePlaygroundItems = async () => {
    try {
      const [siResponse, costResponse] = await Promise.all([
        retrieveFastShippingInstructionsApi(),
        retrieveFastShippingInstructionCostingApi(),
      ]);
      const sis = siResponse?.data || [];
      const costs = costResponse?.data || [];

      // Build a cost lookup map keyed by SI id
      const costMap = {};
      costs.forEach(c => { if (c.id != null) costMap[c.id] = c; });

      const filtered = sis.filter(si => {
        const type = si.shipmentRequestType || '';
        const typeMatches = type.endsWith('IMP') || type.endsWith('INW');
        const storageQty = Number(si.shipmentRequestStorageQuantity);
        return typeMatches && !Number.isNaN(storageQty) && storageQty > 0;
      });

      const mapped = filtered.map(si => {
        const itemId = si.purchaseOrderItemId || si.salesOrderItemId;
        const cost = costMap[si.id] || {};

        // Calculate total cost per KG (excl. storage) same logic as costing view
        const buyingPriceUsd = cost.actualBuyingPriceUsd || cost.purchaseOrderUsdFinalPrice || 0;
        const freightUsd = cost.actualFreightPriceUsd || 0;
        const insuranceUsd = cost.actualInsuranceCostUsd || 0;
        const secondFreightUsd = cost.secondFreightCostUsd || 0;
        const impExpUsd = cost.importExportExpenseUsd || 0;
        const demurrageUsd = cost.demurrageUsd || 0;
        const fumigationUsd = cost.fumigationUsd || 0;
        const handlingUsd = cost.handlingUsd || 0;
        const debitNoteUsd = cost.debitNoteUsd || 0;
        const creditNoteUsd = cost.creditNoteUsd ? -1 * cost.creditNoteUsd : 0;
        let storageCostPerKg = 0;
        if (cost.actualStorageCost) {
          storageCostPerKg = cost.actualStoragePricing === 'Carton' && cost.shipmentRequestNetWeightPerCase
            ? Number(cost.actualStorageCost) / Number(cost.shipmentRequestNetWeightPerCase)
            : Number(cost.actualStorageCost);
        }
        const totalCostExclStorage = buyingPriceUsd + freightUsd + insuranceUsd + secondFreightUsd +
          impExpUsd + demurrageUsd + fumigationUsd + handlingUsd + debitNoteUsd + creditNoteUsd;
        const totalCostInclStorage = totalCostExclStorage + storageCostPerKg;

        return {
          id: si.id,
          siId: si.id,
          siNumber: si.finalShippingInstructionNumber,
          itemId,
          buyingGrade: si.purchaseOrderItemBuyingGrade || si.salesOrderItemBuyingGrade,
          origin: si.purchaseOrderItemOrigin || si.salesOrderItemOrigin,
          crop: si.purchaseOrderItemCrop || si.salesOrderItemCrop,
          nicotine: si.purchaseOrderItemNicotine || si.salesOrderItemNicotine,
          sugar: si.purchaseOrderItemSugar || si.salesOrderItemSugar,
          storageQuantity: si.shipmentRequestStorageQuantity,
          customerGradeMark: si.shipmentRequestCustomerGradeMark,
          shippingInstructionType: si.shippingInstructionType,
          // Cost fields
          buyingPriceUsd: buyingPriceUsd || null,
          totalCostExclStorageUsd: totalCostExclStorage || null,
          totalCostInclStorageUsd: totalCostInclStorage || null,
          salesPriceUsd: cost.salesOrderUsdFinalPrice || null,
          salesPriceOriginal: cost.salesOrderFinalPrice || null,
          salesPriceCurrency: cost.salesOrderFinalPriceCurrency || null,
        };
      });
      setPlaygroundAvailableItems(mapped);
    } catch (error) {
      console.error('Error loading playground items:', error);
      toast.error('Failed to load playground items');
    }
  };

  const handleShippingInstructionChange = async (shippingInstruction) => {
    setNewItem(prev => ({
      ...prev,
      shippingInstruction: shippingInstruction,
      shippingInstructionId: shippingInstruction?.id
    }));

    if (shippingInstruction && !newItem.item) {
      try {
        const siDetails = await retrieveShippingInstructionByIdApi(shippingInstruction.id);
        let selectedItem = null;
        if (siDetails.data.shipmentRequest?.purchaseOrder?.offer?.item) {
          selectedItem = siDetails.data.shipmentRequest.purchaseOrder.offer.item;
        } else if (siDetails.data.shipmentRequest?.salesOrder?.offer?.item) {
          selectedItem = siDetails.data.shipmentRequest.salesOrder.offer.item;
        }
        if (selectedItem) {
          setNewItem(prev => ({ ...prev, item: selectedItem, itemId: selectedItem.id }));
          setAvailableItems([selectedItem]);
        } else {
          retrieveItems();
        }
      } catch (error) {
        console.error('Error loading connected items:', error);
        retrieveItems();
      }
    }
  };

  // ─── CRUD handlers ───────────────────────────────────────────────────
  const handleEditBlend = async () => {
    try {
      await updateBlendApi(selectedBlend, selectedBlendDetails);
      toast.success('Blend updated successfully');
      setEditBlendDialog(false);
      loadBlends();
    } catch (error) {
      toast.error('Failed to update blend');
    }
  };

  const handleEditVersion = async () => {
    try {
      await updateBlendVersionApi(selectedBlend, selectedVersionForEdit.id, selectedVersionForEdit);
      toast.success('Version updated successfully');
      setEditVersionDialog(false);
      const versionsResponse = await retrieveBlendVersionsApi(selectedBlend);
      setVersions(versionsResponse.data);
    } catch (error) {
      toast.error('Failed to update version');
    }
  };

  const handleEditItem = async () => {
    try {
      await updateVersionItemApi(selectedBlend, selectedVersionForEdit.id, selectedItemForEdit.id, selectedItemForEdit);
      toast.success('Item updated successfully');
      setEditItemDialog(false);
      const itemsResponse = await retrieveVersionItemsApi(selectedBlend, selectedVersionForEdit.id);
      const rows = itemsResponse.data.map(mapVersionItem);
      setItemsByVersion(prev => ({ ...prev, [selectedVersionForEdit.id]: rows }));
    } catch (error) {
      toast.error('Failed to update item');
    }
  };

  const handleDeleteBlend = async () => {
    if (window.confirm('Are you sure you want to delete this blend?')) {
      try {
        await deleteBlendApi(selectedBlend);
        toast.success('Blend deleted successfully');
        setSelectedBlend('');
        setSelectedBlendDetails(null);
        setVersions([]);
        loadBlends();
      } catch (error) {
        toast.error('Failed to delete blend');
      }
    }
  };

  const handleDeleteVersion = async (versionId) => {
    if (window.confirm('Are you sure you want to delete this version?')) {
      try {
        await deleteBlendVersionApi(selectedBlend, versionId);
        toast.success('Version deleted successfully');
        const versionsResponse = await retrieveBlendVersionsApi(selectedBlend);
        setVersions(versionsResponse.data);
      } catch (error) {
        toast.error('Failed to delete version');
      }
    }
  };

  const handleDuplicateVersion = async (originalVersion) => {
    setVersionToDuplicate(originalVersion);
    setDuplicateVersionName(`${originalVersion.versionName} (Copy)`);
    setDuplicateVersionDialog(true);
  };

  const executeVersionDuplication = async () => {
    try {
      const duplicatedVersion = {
        versionName: duplicateVersionName,
        description: `${versionToDuplicate.description || ''} - Duplicated from version ${versionToDuplicate.versionName}`,
        status: versionToDuplicate.status || 'Actual',
        cutWidth: versionToDuplicate.cutWidth || '',
        moisture: versionToDuplicate.moisture || '',
        temperature: versionToDuplicate.temperature || '',
        nicotineTolerancePct: versionToDuplicate.nicotineTolerancePct ?? '',
        sugarTolerancePct: versionToDuplicate.sugarTolerancePct ?? ''
      };

      const versionResponse = await createBlendVersionApi(selectedBlend, duplicatedVersion);
      const newVersionId = versionResponse.data.id;

      let originalItems = itemsByVersion[versionToDuplicate.id] || [];

      if (originalItems.length === 0) {
        try {
          const itemsResponse = await retrieveVersionItemsApi(selectedBlend, versionToDuplicate.id);
          originalItems = itemsResponse.data.map(mapVersionItem);
          setItemsByVersion(prev => ({ ...prev, [versionToDuplicate.id]: originalItems }));
        } catch (error) {
          console.error('Error loading original version items:', error);
        }
      }

      if (originalItems.length > 0) {
        const itemCreationPromises = originalItems.map(async item => {
          const itemToCreate = {
            description: item.description,
            quantity: item.quantity,
            unit: item.unit,
            shippingInstruction: item.shippingInstructionId ? { id: item.shippingInstructionId } : null
          };
          if (item.item) { itemToCreate.item = item.item; }
          else if (item.itemId) { itemToCreate.item = { id: item.itemId }; }
          else { console.warn('Item has no item reference:', item); return null; }
          return createVersionItemApi(selectedBlend, newVersionId, itemToCreate);
        });

        const validPromises = itemCreationPromises.filter(p => p !== null);
        if (validPromises.length > 0) await Promise.all(validPromises);

        const itemsResponse = await retrieveVersionItemsApi(selectedBlend, newVersionId);
        const newItems = itemsResponse.data.map(mapVersionItem);
        setItemsByVersion(prev => ({ ...prev, [newVersionId]: newItems }));
        toast.success(`Version duplicated successfully with ${originalItems.length} items as "${duplicateVersionName}"`);
      } else {
        toast.success(`Version duplicated successfully as "${duplicateVersionName}" (no items to copy)`);
      }

      const versionsResponse = await retrieveBlendVersionsApi(selectedBlend);
      setVersions(versionsResponse.data);
      setDuplicateVersionDialog(false);
      setVersionToDuplicate(null);
      setDuplicateVersionName('');
    } catch (error) {
      console.error('Error duplicating version:', error);
      toast.error('Failed to duplicate version');
    }
  };

  const handleDeleteItem = async (versionId, itemId) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await deleteVersionItemApi(selectedBlend, versionId, itemId);
        toast.success('Item deleted successfully');
        const itemsResponse = await retrieveVersionItemsApi(selectedBlend, versionId);
        const rows = itemsResponse.data.map(mapVersionItem);
        setItemsByVersion(prev => ({ ...prev, [versionId]: rows }));
      } catch (error) {
        toast.error('Failed to delete item');
      }
    }
  };

  const handleCreateBlend = async () => {
    try {
      await createBlendApi(newBlend);
      toast.success('Blend created successfully');
      setOpenBlendDialog(false);
      loadBlends();
    } catch (error) {
      toast.error('Failed to create blend');
    }
  };

  const handleCreateVersion = async () => {
    try {
      const response = await createBlendVersionApi(selectedBlend, newVersion);
      const newVersionId = response.data.id;

      if (createFromVersion && selectedBaseVersion) {
        let baseItems = itemsByVersion[selectedBaseVersion.id] || [];

        if (baseItems.length === 0) {
          try {
            const itemsResponse = await retrieveVersionItemsApi(selectedBlend, selectedBaseVersion.id);
            baseItems = itemsResponse.data.map(mapVersionItem);
            setItemsByVersion(prev => ({ ...prev, [selectedBaseVersion.id]: baseItems }));
          } catch (error) {
            console.error('Error loading base version items:', error);
          }
        }

        if (baseItems.length > 0) {
          const itemCreationPromises = baseItems.map(async item => {
            const itemToCreate = {
              description: item.description,
              quantity: item.quantity,
              unit: item.unit,
              shippingInstruction: item.shippingInstructionId ? { id: item.shippingInstructionId } : null
            };
            if (item.item) { itemToCreate.item = item.item; }
            else if (item.itemId) { itemToCreate.item = { id: item.itemId }; }
            else { console.warn('Item has no item reference:', item); return null; }
            return createVersionItemApi(selectedBlend, newVersionId, itemToCreate);
          });

          const validPromises = itemCreationPromises.filter(p => p !== null);
          if (validPromises.length > 0) {
            await Promise.all(validPromises);
            const itemsResponse = await retrieveVersionItemsApi(selectedBlend, newVersionId);
            const newItems = itemsResponse.data.map(mapVersionItem);
            setItemsByVersion(prev => ({ ...prev, [newVersionId]: newItems }));
          }
          toast.success(`Version created successfully with ${validPromises.length} items from "${selectedBaseVersion.versionName}"`);
        } else {
          toast.success('Version created successfully (no items to copy from base version)');
        }
      } else {
        toast.success('Version created successfully');
      }

      setOpenVersionDialog(false);
      resetNewVersion();
      const versionsResponse = await retrieveBlendVersionsApi(selectedBlend);
      setVersions(versionsResponse.data);
    } catch (error) {
      console.error('Error creating version:', error);
      toast.error('Failed to create version');
    }
  };

  const handleCreateItem = async () => {
    try {
      if (!newItem.item || !newItem.item.id) {
        toast.error('Please select an item before creating');
        return;
      }

      const selectedVersion = versions.find(v => v.id === openItemDialog);
      const itemToCreate = {
        description: newItem.description,
        quantity: newItem.quantity,
        unit: newItem.unit,
        item: newItem.item,
        shippingInstruction: newItem.shippingInstructionId ? { id: newItem.shippingInstructionId } : null
      };

      await createVersionItemApi(selectedBlend, selectedVersion.id, itemToCreate);
      toast.success('Item created successfully');
      setOpenItemDialog(false);
      resetNewItem();
      const itemsResponse = await retrieveVersionItemsApi(selectedBlend, selectedVersion.id);
      const rows = itemsResponse.data.map(mapVersionItem);
      setItemsByVersion(prev => ({ ...prev, [selectedVersion.id]: rows }));
    } catch (error) {
      console.error('Error creating item:', error);
      toast.error('Failed to create item');
    }
  };

  const handlePlaygroundSave = (items) => {
    Promise.all(
      items.map(item => {
        const itemToCreate = {
          quantity: item.quantity,
          unit: item.unit || 'KG',
          item: item.itemId ? { id: item.itemId } : null,
          shippingInstruction: item.siId ? { id: item.siId } : null,
        };
        return createVersionItemApi(selectedBlend, openItemDialog, itemToCreate);
      })
    ).then(() => {
      toast.success('Items added successfully');
      setOpenItemDialog(false);
      resetNewItem();
      retrieveVersionItemsApi(selectedBlend, openItemDialog).then(response => {
        const rows = response.data.map(mapVersionItem);
        setItemsByVersion(prev => ({ ...prev, [openItemDialog]: rows }));
      });
    }).catch(() => {
      toast.error('Failed to add some items');
    });
  };

  // ─── Excel import handlers ───────────────────────────────────────────
  const handleRowSIChange = async (rowIdx, newSI) => {
    const updatedRows = excelImportRows.map((r, i) =>
      i === rowIdx ? { ...r, shippingInstruction: newSI, item: null, validated: false, error: null, validating: true } : r
    );
    setExcelImportRows(updatedRows);

    try {
      const siDetails = await retrieveShippingInstructionByIdApi(newSI.id);
      let foundItem = null;
      if (siDetails.data.shipmentRequest?.purchaseOrder?.offer?.item) {
        foundItem = siDetails.data.shipmentRequest.purchaseOrder.offer.item;
      } else if (siDetails.data.shipmentRequest?.salesOrder?.offer?.item) {
        foundItem = siDetails.data.shipmentRequest.salesOrder.offer.item;
      }
      setExcelImportRows(prev => prev.map((r, i) =>
        i === rowIdx
          ? { ...r, item: foundItem || null, validated: !!foundItem, error: foundItem ? null : `No item found in SI #${newSI.id}`, validating: false }
          : r
      ));
    } catch (err) {
      setExcelImportRows(prev => prev.map((r, i) =>
        i === rowIdx
          ? { ...r, item: null, validated: false, error: `Failed to fetch SI: ${err.message || 'Unknown error'}`, validating: false }
          : r
      ));
    }
  };

  const handleValidateExcelRows = async () => {
    try {
      if (!tableData || tableData.length === 0) { toast.error('No Excel data to validate'); return; }
      setValidatingExcel(true);

      const thirdCell = tableData[0]?.[2] ?? '';
      const thirdCellIsNumber = !isNaN(parseFloat(thirdCell.toString().replace(',', '.')));
      const startIndex = thirdCellIsNumber ? 0 : 1;
      const rowsToValidate = tableData.slice(startIndex);
      if (rowsToValidate.length === 0) { toast.error('No data rows found in Excel'); setValidatingExcel(false); return; }

      const parsedRows = rowsToValidate.map((row, idx) => {
        const [rawCGM, , rawQuantity, rawUnit] = row;
        const customerGradeMark = (rawCGM || '').toString().trim();
        const quantity = parseFloat((rawQuantity || '').toString().replace(',', '.'));
        const unit = (rawUnit || '').toString().trim() || 'KG';
        return {
          rowIndex: idx, customerGradeMark, quantity: isNaN(quantity) ? 0 : quantity, unit,
          shippingInstruction: null, sufficientSIs: [], item: null, validated: false, validating: true,
          error: !customerGradeMark ? 'Missing Customer Grade Mark' : (!quantity || isNaN(quantity) ? 'Missing/Invalid Quantity' : null)
        };
      });
      setExcelImportRows([...parsedRows]);

      const updatedRows = [...parsedRows];
      for (let i = 0; i < updatedRows.length; i++) {
        const row = updatedRows[i];
        if (row.error) { row.validating = false; setExcelImportRows([...updatedRows]); continue; }

        try {
          const filterDto = { shipmentRequestCustomerGradeMark: row.customerGradeMark };
          const siResponse = await retrieveFastFilteredShippingInstructionsApi(filterDto);
          const rawMatchingSIs = siResponse.data || [];
          const matchingSIs = rawMatchingSIs.filter(hasPositiveStorageQuantity);

          if (matchingSIs.length === 0) {
            row.error = rawMatchingSIs.length
              ? `No Shipping Instruction with storage quantity > 0 found for CGM "${row.customerGradeMark}"`
              : `No Shipping Instruction found for CGM "${row.customerGradeMark}"`;
            row.validating = false; row.validated = false;
            setExcelImportRows([...updatedRows]); continue;
          }

          const sortedMatchingSIs = sortShippingInstructionsById(matchingSIs);
          const knownSufficientSIs = sortedMatchingSIs.filter(si => Number(si.shipmentRequestStorageQuantity) >= row.quantity);
          row.allMatchingSIs = sortedMatchingSIs.map(si => ({ ...si, _sufficient: Number(si.shipmentRequestStorageQuantity) >= row.quantity }));
          row.sufficientSIs = row.allMatchingSIs;

          if (knownSufficientSIs.length === 0) {
            row.error = `Warning: No SI has sufficient storage qty (need ${row.quantity}). Please review selection.`;
          }

          const defaultSI = sortedMatchingSIs[0];
          row.shippingInstruction = row.allMatchingSIs.find(si => String(si.id) === String(defaultSI.id)) || row.allMatchingSIs[0];

          const siDetails = await retrieveShippingInstructionByIdApi(defaultSI.id);
          let foundItem = null;
          if (siDetails.data.shipmentRequest?.purchaseOrder?.offer?.item) {
            foundItem = siDetails.data.shipmentRequest.purchaseOrder.offer.item;
          } else if (siDetails.data.shipmentRequest?.salesOrder?.offer?.item) {
            foundItem = siDetails.data.shipmentRequest.salesOrder.offer.item;
          }

          if (foundItem) { row.item = foundItem; row.validated = true; row.error = null; }
          else { row.error = `No item found in SI #${defaultSI.id} for CGM "${row.customerGradeMark}"`; row.validated = false; }

          row.validating = false;
          setExcelImportRows([...updatedRows]);
        } catch (error) {
          console.error(`Error validating row ${i}:`, error);
          row.error = `Validation failed: ${error.message || 'Unknown error'}`;
          row.validating = false; row.validated = false;
          setExcelImportRows([...updatedRows]);
        }
      }

      const validCount = updatedRows.filter(r => r.validated).length;
      const errorCount = updatedRows.filter(r => r.error).length;
      if (validCount > 0 && errorCount === 0) toast.success(`All ${validCount} row(s) validated successfully`);
      else if (validCount > 0) toast.success(`${validCount} row(s) validated, ${errorCount} row(s) have errors`);
      else toast.error('No rows could be validated');
      setValidatingExcel(false);
    } catch (error) {
      console.error('Error validating Excel rows:', error);
      toast.error('Failed to validate Excel data');
      setValidatingExcel(false);
    }
  };

  const handleCreateItemsFromExcel = async () => {
    try {
      if (!openItemDialog) { toast.error('No version selected'); return; }
      const validRows = excelImportRows.filter(r => r.validated && r.item && r.shippingInstruction);
      if (validRows.length === 0) { toast.error('No validated rows to import. Please validate first.'); return; }

      const versionId = openItemDialog;
      const creationPromises = validRows.map(row => {
        const itemToCreate = {
          description: '',
          quantity: row.quantity,
          unit: row.unit,
          item: row.item,
          shippingInstruction: row.shippingInstruction ? { id: row.shippingInstruction.id } : null
        };
        return createVersionItemApi(selectedBlend, versionId, itemToCreate);
      });

      await Promise.all(creationPromises);
      toast.success(`Imported ${creationPromises.length} item(s) from Excel`);

      const itemsResponse = await retrieveVersionItemsApi(selectedBlend, versionId);
      const rows = itemsResponse.data.map(mapVersionItem);
      setItemsByVersion(prev => ({ ...prev, [versionId]: rows }));

      setOpenItemDialog(false);
      resetNewItem();
      setTableData([]);
      setExcelImportRows([]);
    } catch (error) {
      console.error('Error importing items from Excel:', error);
      toast.error('Failed to import items from Excel');
    }
  };

  // ─── Effects ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (openItemDialog) { retrieveItems(); retrieveShippingInstructions(); retrievePlaygroundItems(); }
  }, [openItemDialog]);

  useEffect(() => { loadBlends(); }, []);

  const loadBlends = async () => {
    setLoading(true);
    try {
      const response = await retrieveAllBlendsApi();
      console.log('Blends API Response:', response);
      setBlends(response.data);
    } catch (error) {
      console.error('Error loading blends:', error);
      toast.error('Failed to load blends');
    } finally {
      setLoading(false);
    }
  };

  const handleBlendChange = async (e) => {
    const blendId = e.target.value;
    setSelectedBlend(blendId);
    setVersions([]);
    setItemsByVersion({});
    setExpandedVersionId(false);
    setActiveBatchVersionIds(new Set());
    try {
      const [blendResponse, versionsResponse] = await Promise.all([
        retrieveBlendByIdApi(blendId),
        retrieveBlendVersionsApi(blendId)
      ]);
      console.log("successfully retrieved blend and versions", blendResponse, versionsResponse);
      setSelectedBlendDetails(blendResponse.data);
      setVersions(versionsResponse.data);
    } catch (error) {
      toast.error('Failed to load blend details or versions');
    }
  };

  const handleAccordionExpand = async (versionId) => {
    console.log('Version ID:', versionId);
    if (!versionId) { console.error('No version ID provided'); return; }
    const numericVersionId = Number(versionId);
    try {
      const promises = [];
      if (!itemsByVersion[versionId]) {
        promises.push(
          retrieveVersionItemsApi(selectedBlend, numericVersionId).then(res => {
            const rows = res.data.map(mapVersionItem);
            setItemsByVersion(prev => ({ ...prev, [versionId]: rows }));
          })
        );
      }
      if (!activeBatchVersionIds.has(versionId)) {
        promises.push(
          retrieveBatchesByBlendVersionApi(selectedBlend, numericVersionId).then(res => {
            if ((res?.data || []).length > 0) {
              setActiveBatchVersionIds(prev => new Set([...prev, versionId]));
            }
          }).catch(() => {})
        );
      }
      await Promise.all(promises);
    } catch (error) {
      console.error('Error details:', error);
      toast.error('Failed to load items');
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────
  return (
    <Box p={2}>
      <Paper elevation={2}>
        {/* Toolbar */}
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {selectedBlendDetails ? (
              <Stack>
                <Typography variant="h6" component="span">
                  Blend: {selectedBlendDetails.code || selectedBlendDetails.id}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Description: {selectedBlendDetails.description || 'No description'} | ID: {selectedBlendDetails.id}
                </Typography>
              </Stack>
            ) : 'Blends'}
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button startIcon={<AddIcon />} size="small" onClick={() => setOpenBlendDialog(true)}>
              New Blend
            </Button>
            {selectedBlend && (
              <>
                <IconButton size="small" onClick={() => setEditBlendDialog(true)}><EditIcon /></IconButton>
                <IconButton size="small" onClick={handleDeleteBlend}><DeleteIcon /></IconButton>
                <Button startIcon={<AddIcon />} size="small" onClick={() => setOpenVersionDialog(true)}>
                  New Version
                </Button>
              </>
            )}
            {(hasActiveBlendFilters() || hasActiveVersionFilters() || hasActiveItemFilters()) && (
              <Button size="small" startIcon={<ClearIcon />} onClick={clearAllFilters} color="secondary">
                Clear All Filters
              </Button>
            )}
          </Stack>
          <FormControl size="small" sx={{ minWidth: 300 }}>
            <Autocomplete
              options={filteredBlends}
              value={blends.find(b => b.id === selectedBlend) || null}
              onChange={(event, newValue) => {
                if (newValue) {
                  handleBlendChange({ target: { value: newValue.id } });
                } else {
                  setSelectedBlend('');
                  setSelectedBlendDetails(null);
                  setVersions([]);
                  setItemsByVersion({});
                  setExpandedVersionId(false);
                }
              }}
              getOptionLabel={(option) => `${option.code || option.id} - ${option.description || 'No description'}`}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Stack>
                    <Typography variant="body2" fontWeight="bold">Blend: {option.code || option.id}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: {option.id} | Description: {option.description || 'No description'}
                    </Typography>
                  </Stack>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Blend"
                  placeholder="Type to search blends..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>)
                  }}
                />
              )}
              filterOptions={(options, { inputValue }) => {
                if (!inputValue) return options;
                const searchTerm = inputValue.toLowerCase();
                return options.filter(option =>
                  (option.code && option.code.toLowerCase().includes(searchTerm)) ||
                  (option.description && option.description.toLowerCase().includes(searchTerm)) ||
                  option.id.toString().includes(searchTerm)
                );
              }}
              size="small"
              clearOnEscape
              selectOnFocus
              handleHomeEndKeys
            />
          </FormControl>
        </Toolbar>

        {/* Blend Filters */}
        <Box px={2} pb={2}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="subtitle2">Blend Filters</Typography>
            {hasActiveBlendFilters() && (
              <Chip size="small" label={`${filteredBlends.length}/${blends.length}`} color="primary" variant="outlined" />
            )}
            {loadingBlendFilterData && <Chip size="small" label="Loading item/SI data..." variant="outlined" />}
            <IconButton
              size="small"
              onClick={() => setShowBlendFilters(!showBlendFilters)}
              color={showBlendFilters || hasActiveBlendFilters() ? 'primary' : 'default'}
            >
              <FilterListIcon />
            </IconButton>
          </Stack>
          <Collapse in={showBlendFilters}>
            <Box p={2} mt={1} bgcolor="grey.50" borderRadius={1}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={blendFilterOptions.items}
                    value={blendFilter.selectedItems}
                    onChange={(_event, newValue) => setBlendFilter((prev) => ({ ...prev, selectedItems: newValue }))}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.key === value.key}
                    renderInput={(params) => <TextField {...params} label="Items" placeholder="Select item(s)" />}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={blendFilterOptions.shippingInstructions}
                    value={blendFilter.selectedShippingInstructions}
                    onChange={(_event, newValue) => setBlendFilter((prev) => ({ ...prev, selectedShippingInstructions: newValue }))}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.key === value.key}
                    renderInput={(params) => <TextField {...params} label="Shipping Instructions" placeholder="Select SI(s)" />}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Button
                    size="small"
                    startIcon={<ClearIcon />}
                    onClick={clearBlendFilters}
                    disabled={!blendFilter.selectedItems.length && !blendFilter.selectedShippingInstructions.length}
                  >
                    Clear
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Box>

        {/* Version Filters */}
        {selectedBlend && (
          <>
            <Box p={2} bgcolor="grey.100">
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="h6">Versions</Typography>
                {hasActiveVersionFilters() && (
                  <Chip size="small" label={`${filteredVersions.length}/${versions.length}`} color="primary" variant="outlined" />
                )}
                {loadingVersionFilterData && <Chip size="small" label="Loading item/SI data..." variant="outlined" />}
                <IconButton
                  size="small"
                  onClick={() => setShowVersionFilters(!showVersionFilters)}
                  color={showVersionFilters || hasActiveVersionFilters() ? 'primary' : 'default'}
                >
                  <FilterListIcon />
                </IconButton>
              </Stack>
            </Box>
            <Collapse in={showVersionFilters}>
              <Box p={2} bgcolor="grey.50">
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      size="small" fullWidth label="Version Name"
                      value={versionFilter.name}
                      onChange={(e) => setVersionFilter(prev => ({ ...prev, name: e.target.value }))}
                      InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={versionFilter.status} label="Status"
                        onChange={(e) => setVersionFilter(prev => ({ ...prev, status: e.target.value }))}
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="Actual">Actual</MenuItem>
                        <MenuItem value="Sample">Sample</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      size="small" fullWidth label="Date From" type="date"
                      value={versionFilter.dateFrom}
                      onChange={(e) => setVersionFilter(prev => ({ ...prev, dateFrom: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      size="small" fullWidth label="Date To" type="date"
                      value={versionFilter.dateTo}
                      onChange={(e) => setVersionFilter(prev => ({ ...prev, dateTo: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Autocomplete
                      multiple
                      size="small"
                      options={versionFilterOptions.items}
                      value={versionFilter.selectedItems}
                      onChange={(_event, newValue) => setVersionFilter((prev) => ({ ...prev, selectedItems: newValue }))}
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(option, value) => option.key === value.key}
                      renderInput={(params) => <TextField {...params} label="Items" placeholder="Select item(s)" />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Autocomplete
                      multiple
                      size="small"
                      options={versionFilterOptions.shippingInstructions}
                      value={versionFilter.selectedShippingInstructions}
                      onChange={(_event, newValue) => setVersionFilter((prev) => ({ ...prev, selectedShippingInstructions: newValue }))}
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(option, value) => option.key === value.key}
                      renderInput={(params) => <TextField {...params} label="Shipping Instructions" placeholder="Select SI(s)" />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Button
                      size="small" startIcon={<ClearIcon />} onClick={clearVersionFilters}
                      disabled={!versionFilter.name && !versionFilter.status && !versionFilter.dateFrom && !versionFilter.dateTo && !versionFilter.selectedItems.length && !versionFilter.selectedShippingInstructions.length}
                    >
                      Clear
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </>
        )}

        {/* Version Accordions */}
        {filteredVersions.map(v => (
          <Box key={v.id} id={`blend-version-${v.id}`}>
            <VersionAccordion
              version={v}
              items={itemsByVersion[v.id] || []}
              expanded={expandedVersionId === v.id}
              itemFilter={itemFilter}
              setItemFilter={setItemFilter}
              showItemFilters={showItemFilters}
              setShowItemFilters={setShowItemFilters}
              hasActiveItemFilters={hasActiveItemFilters}
              clearItemFilters={clearItemFilters}
              getFilteredItems={getFilteredItems}
              onExpand={(versionId, isExpanded) => {
                setExpandedVersionId(isExpanded ? versionId : false);
                if (isExpanded) handleAccordionExpand(versionId);
              }}
              onEditVersion={(ver) => { setSelectedVersionForEdit(ver); setEditVersionDialog(true); }}
              onDuplicateVersion={handleDuplicateVersion}
              onDeleteVersion={handleDeleteVersion}
              hasActiveBatch={activeBatchVersionIds.has(v.id)}
              onAddItem={(versionId) => setOpenItemDialog(versionId)}
              onEditItem={(row, ver) => { setSelectedItemForEdit(row); setSelectedVersionForEdit(ver); setEditItemDialog(true); }}
              onDeleteItem={handleDeleteItem}
            />
          </Box>
        ))}

        {selectedBlend && versions.length === 0 && (
          <Box p={2}><Typography>No versions found for this blend.</Typography></Box>
        )}

        {/* ─── Dialogs ────────────────────────────────────────────────── */}
        <CreateBlendDialog
          open={openBlendDialog}
          onClose={() => setOpenBlendDialog(false)}
          newBlend={newBlend}
          setNewBlend={setNewBlend}
          onSubmit={handleCreateBlend}
        />

        <EditBlendDialog
          open={editBlendDialog}
          onClose={() => setEditBlendDialog(false)}
          blendDetails={selectedBlendDetails}
          setBlendDetails={setSelectedBlendDetails}
          onSubmit={handleEditBlend}
        />

        <CreateVersionDialog
          open={openVersionDialog}
          onClose={() => { setOpenVersionDialog(false); resetNewVersion(); }}
          newVersion={newVersion}
          setNewVersion={setNewVersion}
          createFromVersion={createFromVersion}
          setCreateFromVersion={setCreateFromVersion}
          selectedBaseVersion={selectedBaseVersion}
          setSelectedBaseVersion={setSelectedBaseVersion}
          versions={versions}
          itemsByVersion={itemsByVersion}
          onSubmit={handleCreateVersion}
        />

        <EditVersionDialog
          open={editVersionDialog}
          onClose={() => setEditVersionDialog(false)}
          versionForEdit={selectedVersionForEdit}
          setVersionForEdit={setSelectedVersionForEdit}
          onSubmit={handleEditVersion}
        />

        <DuplicateVersionDialog
          open={duplicateVersionDialog}
          onClose={() => setDuplicateVersionDialog(false)}
          versionToDuplicate={versionToDuplicate}
          duplicateVersionName={duplicateVersionName}
          setDuplicateVersionName={setDuplicateVersionName}
          itemsByVersion={itemsByVersion}
          onSubmit={executeVersionDuplication}
        />

        <EditItemDialog
          open={editItemDialog}
          onClose={() => setEditItemDialog(false)}
          itemForEdit={selectedItemForEdit}
          setItemForEdit={setSelectedItemForEdit}
          onSubmit={handleEditItem}
        />

        <AddItemDialog
          open={openItemDialog}
          onClose={() => { setOpenItemDialog(false); resetNewItem(); setExcelImportRows([]); }}
          newItem={newItem}
          setNewItem={setNewItem}
          availableItems={availableItems}
          availableShippingInstructions={availableShippingInstructions}
          onShippingInstructionChange={handleShippingInstructionChange}
          retrieveItems={retrieveItems}
          retrieveShippingInstructions={retrieveShippingInstructions}
          onCreateItem={handleCreateItem}
          addItemTab={addItemTab}
          setAddItemTab={setAddItemTab}
          currentItems={itemsByVersion[openItemDialog] || []}
          onPlaygroundSave={handlePlaygroundSave}
          playgroundAvailableItems={playgroundAvailableItems}
          versionSettings={versions.find(v => v.id === openItemDialog) || null}
          tableData={tableData}
          setTableData={setTableData}
          excelImportRows={excelImportRows}
          setExcelImportRows={setExcelImportRows}
          validatingExcel={validatingExcel}
          onRowSIChange={handleRowSIChange}
          onValidateExcelRows={handleValidateExcelRows}
          onCreateItemsFromExcel={handleCreateItemsFromExcel}
        />
      </Paper>
    </Box>
  );
}
